import { useEffect, useRef, useState } from "react";
import { Calendar, Check, ChevronDown, Search, X } from "lucide-react";

import { Button } from "../Button/Button";
import { formatDate } from "../../lib/format";

import styles from "./FilterBar.module.css";

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  dateValue?: string;
  onDateChange?: (value: string) => void;
  datePlaceholder?: string;
  estadoValue?: string;
  onEstadoChange?: (value: string) => void;
  estadoOptions?: { value: string; label: string }[];
  estadoPlaceholder?: string;
  tipoValues?: string[];
  onTipoChange?: (values: string[]) => void;
  tipoOptions?: string[];
  tipoPlaceholder?: string;
  onApply?: () => void;
  onClear?: () => void;
  trailing?: React.ReactNode;
}

export function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "pesquisar…",
  dateValue,
  onDateChange,
  datePlaceholder = "Data de criação",
  estadoValue,
  onEstadoChange,
  estadoOptions,
  estadoPlaceholder = "Estado",
  tipoValues = [],
  onTipoChange,
  tipoOptions,
  tipoPlaceholder = "Tipo de fatura",
  onApply,
  onClear,
  trailing,
}: FilterBarProps) {
  const dateRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tipoRef = useRef<HTMLDivElement>(null);
  const [estadoOpen, setEstadoOpen] = useState(false);
  const [tipoOpen, setTipoOpen] = useState(false);

  useEffect(() => {
    if (!estadoOpen && !tipoOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setEstadoOpen(false);
      }
      if (tipoRef.current && !tipoRef.current.contains(event.target as Node)) {
        setTipoOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setEstadoOpen(false);
        setTipoOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [estadoOpen, tipoOpen]);

  function toggleTipo(value: string) {
    onTipoChange?.(
      tipoValues.includes(value) ? tipoValues.filter((v) => v !== value) : [...tipoValues, value],
    );
  }

  const tipoLabel =
    tipoValues.length === 0
      ? tipoPlaceholder
      : tipoValues.length <= 2
        ? tipoValues.join(", ")
        : `${tipoValues.length} tipos`;

  function openDatePicker() {
    const input = dateRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch {
        // fallthrough
      }
    }
    input.focus();
    input.click();
  }

  function clearDate(event: React.MouseEvent) {
    event.stopPropagation();
    onDateChange?.("");
  }

  const selectedEstado = estadoOptions?.find((o) => o.value === estadoValue);

  return (
    <div className={styles.bar}>
      <div className={styles.search}>
        <Search size={14} className={styles.searchIcon} aria-hidden />
        <input
          type="search"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Pesquisar"
        />
      </div>

      {onDateChange && (
        <button
          type="button"
          className={`${styles.chip} ${styles.dateChip}`}
          onClick={openDatePicker}
          aria-label={dateValue ? `Data: ${formatDate(dateValue)}` : datePlaceholder}
        >
          <input
            ref={dateRef}
            type="date"
            value={dateValue ?? ""}
            onChange={(e) => onDateChange(e.target.value)}
            className={styles.dateNative}
            tabIndex={-1}
          />
          <Calendar size={14} aria-hidden />
          <span className={dateValue ? styles.chipValue : styles.chipPlaceholder}>
            {dateValue ? formatDate(dateValue) : datePlaceholder}
          </span>
          {dateValue && (
            <span
              role="button"
              aria-label="Limpar data"
              className={styles.chipClear}
              onClick={clearDate}
            >
              <X size={12} />
            </span>
          )}
        </button>
      )}

      {onEstadoChange && estadoOptions && (
        <div className={styles.dropdown} ref={dropdownRef}>
          <button
            type="button"
            className={`${styles.chip} ${estadoOpen ? styles.chipOpen : ""}`}
            onClick={() => setEstadoOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={estadoOpen}
          >
            <span className={selectedEstado ? styles.chipValue : styles.chipPlaceholder}>
              {selectedEstado?.label ?? estadoPlaceholder}
            </span>
            <ChevronDown
              size={14}
              aria-hidden
              className={`${styles.chevron} ${estadoOpen ? styles.chevronOpen : ""}`}
            />
          </button>
          {estadoOpen && (
            <div className={styles.dropdownMenu} role="listbox">
              <button
                type="button"
                role="option"
                aria-selected={!estadoValue}
                className={`${styles.dropdownItem} ${
                  !estadoValue ? styles.dropdownItemActive : ""
                }`}
                onClick={() => {
                  onEstadoChange("");
                  setEstadoOpen(false);
                }}
              >
                Todos
                {!estadoValue && <Check size={14} />}
              </button>
              {estadoOptions.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === estadoValue}
                  className={`${styles.dropdownItem} ${
                    o.value === estadoValue ? styles.dropdownItemActive : ""
                  }`}
                  onClick={() => {
                    onEstadoChange(o.value);
                    setEstadoOpen(false);
                  }}
                >
                  {o.label}
                  {o.value === estadoValue && <Check size={14} />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {onTipoChange && tipoOptions && (
        <div className={styles.dropdown} ref={tipoRef}>
          <button
            type="button"
            className={`${styles.chip} ${tipoOpen ? styles.chipOpen : ""}`}
            onClick={() => setTipoOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={tipoOpen}
          >
            <span className={tipoValues.length ? styles.chipValue : styles.chipPlaceholder}>
              {tipoLabel}
            </span>
            <ChevronDown
              size={14}
              aria-hidden
              className={`${styles.chevron} ${tipoOpen ? styles.chevronOpen : ""}`}
            />
          </button>
          {tipoOpen && (
            <div
              className={`${styles.dropdownMenu} ${styles.dropdownMenuScroll}`}
              role="listbox"
              aria-multiselectable
            >
              <button
                type="button"
                role="option"
                aria-selected={tipoValues.length === 0}
                className={`${styles.dropdownItem} ${
                  tipoValues.length === 0 ? styles.dropdownItemActive : ""
                }`}
                onClick={() => onTipoChange([])}
              >
                Todos
                {tipoValues.length === 0 && <Check size={14} />}
              </button>
              {tipoOptions.map((t) => {
                const active = tipoValues.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`${styles.dropdownItem} ${active ? styles.dropdownItemActive : ""}`}
                    onClick={() => toggleTipo(t)}
                  >
                    {t}
                    {active && <Check size={14} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {onApply && (
        <Button variant="primary" onClick={onApply}>
          Aplicar
        </Button>
      )}
      {onClear && (
        <Button variant="secondary" onClick={onClear}>
          Limpar filtros
        </Button>
      )}

      {trailing && <div className={styles.trailing}>{trailing}</div>}
    </div>
  );
}
